import { defaultQueryOptions } from 'constants/'
import { useQuery } from 'react-query'
import { QueryOptions } from 'types'
import { useDeviceService } from '../services'
import type { FittingQualityScoreRecord, FittingSnapshotRecord } from '../services'

const hookName = 'useFittingQualityScores'

type FittingQualityScoresMap = Map<string, FittingQualityScoreRecord>

/**
 * Maps raw fitting-snapshot records to minimal, PII-free records keyed by appointment.
 *
 * ⚠️ PII: only the score and the per-criterion details are copied — the raw `snapshot`
 * object (patient name, audiogram, gains, account number) is never read or carried over.
 *
 * When several snapshots exist for one appointment we keep the latest by `createdAt`,
 * decided explicitly here rather than depending on the request's sort order being preserved.
 */
const mapSnapshotsToScores = (records: FittingSnapshotRecord[]): FittingQualityScoresMap => {
  const map: FittingQualityScoresMap = new Map()

  records.forEach(({ appointmentGid, createdAt, fittingQualityScore, fittingQualityScoreDetails }) => {
    if (!appointmentGid) {
      return
    }

    const existing = map.get(appointmentGid)

    if (existing && existing.createdAt >= createdAt) {
      return
    }

    map.set(appointmentGid, {
      appointmentGid,
      createdAt,
      details: fittingQualityScoreDetails ?? null,
      score: fittingQualityScore ?? null,
    })
  })

  return map
}

const useFittingQualityScores = (customerGid: string, options: QueryOptions = { enabled: true }) => {
  const { getFittingSnapshots } = useDeviceService()

  const doQuery = async (): Promise<FittingQualityScoresMap> => {
    // Never log the raw response — it carries decrypted PII (see mapSnapshotsToScores).
    const { data = [] } = await getFittingSnapshots({ customerGid })

    return mapSnapshotsToScores(data)
  }

  return useQuery([hookName, { customerGid }], doQuery, {
    ...defaultQueryOptions,
    ...options,
  })
}

export { mapSnapshotsToScores, useFittingQualityScores }
export type { FittingQualityScoresMap }
